/**
 * Validated Complex Process Form
 * 
 * Fully integrated form with:
 * - AJV validation
 * - GraphQL backend integration
 * - Real-time validation feedback
 * - Business rule validation
 * - Repository pattern usage
 */

import {
    Alert,
    Badge,
    Button,
    Card,
    Divider,
    Group,
    JsonInput,
    MultiSelect,
    NumberInput,
    Paper,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IconCheck,
    IconAlertTriangle,
    IconInfoCircle,
    IconX
} from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';
import {
    ComplexProcessDefinition,
    ProcessStage,
    ProcessConnection,
    ExternalSystem,
    useProcessMutations,
    useProcesses
} from '../data';
import { asBackendId } from '../types/branded-ids';

interface ValidatedComplexProcessFormProps {
    initialData?: Partial<ComplexProcessDefinition>;
    mode: 'create' | 'edit';
    onSuccess?: (data: ComplexProcessDefinition) => void;
    onCancel?: () => void;
}

export const ValidatedComplexProcessForm: React.FC<ValidatedComplexProcessFormProps> = ({
    initialData,
    mode,
    onSuccess,
    onCancel
}) => {
    // Get existing processes for validation context
    const { processes } = useProcesses();
    const { createProcess, updateProcess } = useProcessMutations();

    // Form state
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [previewData, setPreviewData] = useState<any>(null);

    // Initialize validated form
    const form = useValidatedForm<Partial<ComplexProcessDefinition>>({
        schemaName: 'ComplexProcess',
        initialValues: {
            name: initialData?.name || '',
            domain: initialData?.domain || 'Healthcare',
            description: initialData?.description || '',
            complexity: initialData?.complexity || 'Medium',
            stages: initialData?.stages || [],
            connections: initialData?.connections || [],
            actors: initialData?.actors || [],
            externalSystems: initialData?.externalSystems || [],
            isTemplate: initialData?.isTemplate || false,
            ...initialData
        },
        validationContext: {
            tenant: sessionStorage.getItem('tenant') || 'default',
            domain: initialData?.domain,
            existingData: processes,
            userRole: 'admin'
        },
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false,
        onValidationChange: (result) => {
            // Update preview when validation passes
            if (result.valid) {
                setPreviewData(form.values);
            }
        }
    });

    // Form submission with GraphQL
    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'ComplexProcess',
        () => ({
            mutate: async (data: Partial<ComplexProcessDefinition>) => {
                if (mode === 'create') {
                    return await createProcess(data as any);
                } else {
                    return await updateProcess(initialData?.id || '', data);
                }
            },
            loading: false,
            error: null
        })
    );

    // Domain options
    const domainOptions = [
        'Healthcare', 'Finance', 'Supply Chain', 'HR', 'IT', 
        'Manufacturing', 'Insurance', 'Other'
    ];

    // Complexity options with descriptions
    const complexityOptions = [
        { value: 'Low', label: 'Low - Simple linear processes (1-5 steps)' },
        { value: 'Medium', label: 'Medium - Standard processes (6-15 steps)' },
        { value: 'High', label: 'High - Complex processes (16-30 steps)' },
        { value: 'Very High', label: 'Very High - Enterprise processes (30+ steps)' }
    ];

    // Stage type options
    const stageTypeOptions = [
        'Entry', 'Process', 'Decision', 'External', 'Loop', 'Meta', 'Exit'
    ];

    // Handle form submission
    const handleSubmit = async (values: Partial<ComplexProcessDefinition>) => {
        const result = await submitForm(values, form.validationResult.valid ? undefined : {
            tenant: sessionStorage.getItem('tenant') || 'default',
            existingData: processes
        });

        if (result.success && onSuccess) {
            onSuccess(result.data);
        }
    };

    // Add new stage
    const addStage = () => {
        const newStage: ProcessStage = {
            id: asBackendId(`stage_${Date.now()}`), // Temporary ID - backend will generate Snowflake ID on save
            name: '',
            stageType: 'Process',
            actors: [],
            steps: [],
            loops: false
        };

        form.insertListItem('stages', newStage);
    };

    // Add new connection
    const addConnection = () => {
        const newConnection: ProcessConnection = {
            from: '',
            to: '',
            connectionType: 'Sequential'
        };

        form.insertListItem('connections', newConnection);
    };

    // Add external system
    const addExternalSystem = () => {
        const newSystem: ExternalSystem = {
            id: asBackendId(`ext_${Date.now()}`), // Temporary ID - backend will generate Snowflake ID on save
            name: '',
            systemType: 'API',
            integrationPoints: [],
            dataFormats: []
        };

        form.insertListItem('externalSystems', newSystem);
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSubmit(values as Partial<ComplexProcessDefinition>))}>
            <Stack gap="lg">
                {/* Header */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Title order={3}>
                                {mode === 'create' ? 'Create' : 'Edit'} Complex Process
                            </Title>
                            <Text size="sm" c="dimmed">
                                All fields are validated using JSON Schema and AJV
                            </Text>
                        </div>
                        <Group>
                            <Badge 
                                color={form.validationResult.valid ? 'green' : 'red'}
                                leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconX size={14} />}
                            >
                                {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} Error${form.validationResult.errors.length > 1 ? 's' : ''}`}
                            </Badge>
                        </Group>
                    </Group>
                </Paper>

                {/* Validation Summary */}
                {form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}>
                        <Stack gap="xs">
                            {form.validationResult.errors.map((error, index) => (
                                <Text key={index} size="sm">
                                    <strong>{error.field}:</strong> {error.message}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {form.validationResult.warnings && form.validationResult.warnings.length > 0 && (
                    <Alert color="yellow" title="Validation Warnings" icon={<IconInfoCircle />}>
                        <Stack gap="xs">
                            {form.validationResult.warnings.map((warning, index) => (
                                <Text key={index} size="sm">
                                    <strong>{warning.field}:</strong> {warning.message}
                                    {warning.suggestion && <Text c="dimmed"> • {warning.suggestion}</Text>}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Basic Information */}
                <Card withBorder>
                    <Title order={4} mb="md">Basic Information</Title>
                    <Stack gap="md">
                        <TextInput
                            label="Process Name"
                            placeholder="e.g., Healthcare Revenue Cycle Management"
                            required
                            {...form.getInputProps('name')}
                            error={form.getFieldError('name')}
                            rightSection={
                                form.isFieldValid('name') && form.values.name ? 
                                <IconCheck size={16} color="green" /> : undefined
                            }
                        />

                        <Select
                            label="Business Domain"
                            data={domainOptions}
                            required
                            {...form.getInputProps('domain')}
                            error={form.getFieldError('domain')}
                        />

                        <Textarea
                            label="Description"
                            placeholder="Describe the purpose and scope of this process..."
                            required
                            minRows={3}
                            {...form.getInputProps('description')}
                            error={form.getFieldError('description')}
                        />

                        <Select
                            label="Process Complexity"
                            data={complexityOptions}
                            {...form.getInputProps('complexity')}
                            error={form.getFieldError('complexity')}
                        />

                        <Group>
                            <Switch
                                label="Process Template"
                                description="Mark as template for reuse"
                                {...form.getInputProps('isTemplate', { type: 'checkbox' })}
                            />
                            <Switch
                                label="Advanced Options"
                                checked={showAdvanced}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowAdvanced(e.currentTarget.checked)}
                            />
                        </Group>
                    </Stack>
                </Card>

                {/* Actors */}
                <Card withBorder>
                    <Title order={4} mb="md">Process Actors</Title>
                    <MultiSelect
                        label="Actors/Participants"
                        placeholder="Add actors involved in this process"
                        data={['Patient', 'Provider', 'Payer', 'Administrator', 'System', 'Manager']}
                        searchable
                        {...form.getInputProps('actors')}
                        error={form.getFieldError('actors')}
                    />
                </Card>

                {/* Stages */}
                <Card withBorder>
                    <Group justify="space-between" mb="md">
                        <Title order={4}>Process Stages</Title>
                        <Button variant="light" onClick={addStage}>
                            Add Stage
                        </Button>
                    </Group>

                    <Stack gap="md">
                        {(form.values.stages || []).map((stage: any, index: number) => (
                            <Paper key={stage.id || index} p="md" withBorder>
                                <Group justify="space-between" mb="sm">
                                    <Text fw={500}>Stage {index + 1}</Text>
                                    <Button
                                        variant="subtle"
                                        color="red"
                                        size="xs"
                                        onClick={() => form.removeListItem('stages', index)}
                                    >
                                        Remove
                                    </Button>
                                </Group>

                                <Group grow>
                                    <TextInput
                                        label="Stage Name"
                                        placeholder="e.g., Patient Registration"
                                        {...form.getInputProps(`stages.${index}.name`)}
                                        error={form.getFieldError(`stages.${index}.name`)}
                                    />
                                    <Select
                                        label="Stage Type"
                                        data={stageTypeOptions}
                                        {...form.getInputProps(`stages.${index}.stageType`)}
                                        error={form.getFieldError(`stages.${index}.stageType`)}
                                    />
                                </Group>

                                <Switch
                                    label="Contains Loops"
                                    {...form.getInputProps(`stages.${index}.loops`, { type: 'checkbox' })}
                                    mt="sm"
                                />
                            </Paper>
                        ))}

                        {(!form.values.stages || form.values.stages.length === 0) && (
                            <Text c="dimmed" ta="center" py="xl">
                                No stages defined. Click &quot;Add Stage&quot; to get started.
                            </Text>
                        )}
                    </Stack>
                </Card>

                {/* Advanced Options */}
                {showAdvanced && (
                    <>
                        {/* Connections */}
                        <Card withBorder>
                            <Group justify="space-between" mb="md">
                                <Title order={4}>Stage Connections</Title>
                                <Button variant="light" onClick={addConnection}>
                                    Add Connection
                                </Button>
                            </Group>

                            <Stack gap="md">
                                {(form.values.connections || []).map((connection: any, index: number) => (
                                    <Paper key={index} p="md" withBorder>
                                        <Group justify="space-between" mb="sm">
                                            <Text fw={500}>Connection {index + 1}</Text>
                                            <Button
                                                variant="subtle"
                                                color="red"
                                                size="xs"
                                                onClick={() => form.removeListItem('connections', index)}
                                            >
                                                Remove
                                            </Button>
                                        </Group>

                                        <Group grow>
                                            <TextInput
                                                label="From Stage"
                                                {...form.getInputProps(`connections.${index}.from`)}
                                            />
                                            <TextInput
                                                label="To Stage"
                                                {...form.getInputProps(`connections.${index}.to`)}
                                            />
                                            <Select
                                                label="Connection Type"
                                                data={['Sequential', 'Loop', 'Conditional', 'Parallel', 'Escalation']}
                                                {...form.getInputProps(`connections.${index}.connectionType`)}
                                            />
                                        </Group>
                                    </Paper>
                                ))}
                            </Stack>
                        </Card>

                        {/* External Systems */}
                        <Card withBorder>
                            <Group justify="space-between" mb="md">
                                <Title order={4}>External Systems</Title>
                                <Button variant="light" onClick={addExternalSystem}>
                                    Add System
                                </Button>
                            </Group>

                            <Stack gap="md">
                                {(form.values.externalSystems || []).map((system: any, index: number) => (
                                    <Paper key={system.id || index} p="md" withBorder>
                                        <Group justify="space-between" mb="sm">
                                            <Text fw={500}>External System {index + 1}</Text>
                                            <Button
                                                variant="subtle"
                                                color="red"
                                                size="xs"
                                                onClick={() => form.removeListItem('externalSystems', index)}
                                            >
                                                Remove
                                            </Button>
                                        </Group>

                                        <Group grow>
                                            <TextInput
                                                label="System Name"
                                                {...form.getInputProps(`externalSystems.${index}.name`)}
                                            />
                                            <Select
                                                label="System Type"
                                                data={['API', 'Database', 'File', 'Queue', 'Service', 'Other']}
                                                {...form.getInputProps(`externalSystems.${index}.systemType`)}
                                            />
                                        </Group>
                                    </Paper>
                                ))}
                            </Stack>
                        </Card>

                        {/* Data Preview */}
                        {previewData && (
                            <Card withBorder>
                                <Title order={4} mb="md">Data Preview (JSON)</Title>
                                <JsonInput
                                    value={JSON.stringify(previewData, null, 2)}
                                    readOnly
                                    autosize
                                    minRows={10}
                                />
                            </Card>
                        )}
                    </>
                )}

                {/* Form Actions */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="sm" c="dimmed">
                                Form will be validated before submission
                            </Text>
                            {form.isValidating && (
                                <Text size="sm" c="blue">
                                    Validating...
                                </Text>
                            )}
                        </div>
                        
                        <Group>
                            <Button variant="light" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={submitting || form.isValidating}
                                disabled={!form.validationResult.valid}
                            >
                                {mode === 'create' ? 'Create Process' : 'Update Process'}
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Submission Result */}
                {submissionResult && (
                    <Alert 
                        color={submissionResult.success ? 'green' : 'red'}
                        title={submissionResult.success ? 'Success' : 'Error'}
                        onClose={() => {}}
                    >
                        {submissionResult.success ? 
                            'Process saved successfully!' : 
                            submissionResult.error
                        }
                    </Alert>
                )}
            </Stack>
        </form>
    );
};

export default ValidatedComplexProcessForm;